import { type ConsentState, type DataCollectionConfig, type IConsentManager } from '../typings'
import { dataLayerNames } from '../config'
import { type BaseCMP, ConsentModeCMP, CookieBotCMP, CookieScriptCMP } from './cmp'

export class ConsentManager implements IConsentManager {
  private readonly config: DataCollectionConfig
  private currentConsentState: ConsentState | null = null
  private consentUpdateCallbacks: ((state: ConsentState) => void)[] = []
  private cmp: BaseCMP | null = null
  private initialized = false
  private signalReceived = false

  constructor(config: DataCollectionConfig) {
    this.config = config
    this.initialize()
  }

  private initialize(): void {
    if (this.initialized) {
      return
    }

    this.currentConsentState = {
      // @ts-expect-error -- Making sure this is set
      analytics: false,
      // @ts-expect-error -- Making sure this is set
      marketing: false,
      // @ts-expect-error -- Making sure this is set
      preferences: false,
      ...this.config.consentSettings.defaultConsentState,
      client_timestamp: Date.now()
    }

    this.cmp = this.createCMP()

    if (this.cmp) {
      this.cmp.setConsentUpdateCallback(this.updateConsentState.bind(this))
      this.cmp.initialize()
    } else {
      // Fallback: send default consent state when no CMP is configured
      const sendCustomConsentUpdateEvent = () => {
        window.dispatchEvent(
          new CustomEvent('consentUpdate', { detail: this.currentConsentState! })
        )
      }

      if (document.readyState === 'complete') {
        sendCustomConsentUpdateEvent()
      } else {
        window.addEventListener('load', sendCustomConsentUpdateEvent)
      }
    }

    this.initialized = true

    if (this.config.isDebugMode) {
      console.log('Initialized ConsentManager')
    }
  }

  private createCMP(): BaseCMP | null {
    const cmpType = this.config.cmpConfig?.cmpType

    if (!cmpType || cmpType === 'NONE') {
      return null
    }

    const cmpConfig = {
      cmpType,
      cmpId: this.config.cmpConfig?.cmpId,
      isDebugMode: this.config.isDebugMode,
      dataLayerNames: dataLayerNames(this.config.destinations)
    }

    switch (cmpType) {
      case 'COOKIE_BOT':
        return new CookieBotCMP(cmpConfig)
      case 'COOKIE_SCRIPT':
        return new CookieScriptCMP(cmpConfig)
      case 'CONSENT_MODE':
        return new ConsentModeCMP(cmpConfig)
      default:
        // `cmpType` is a free-form String on the backend, so an unrecognised
        // value must degrade to "no consent signal", never take the page down.
        if (this.config.isDebugMode) {
          console.warn(`Unsupported CMP type: ${cmpType as string}`)
        }
        return null
    }
  }

  private handleCustomConsentUpdate = ((event: CustomEvent<ConsentState>) => {
    try {
      if (!event?.detail) throw new Error('Invalid consent update event')
      this.updateConsentState(event.detail)
    } catch (err) {
      console.error('Failed to process consent update:', err)
    }
  }) as EventListener

  public getCurrentConsentState(): ConsentState {
    if (!this.initialized) {
      throw new Error('ConsentManager not initialized')
    }
    if (!this.currentConsentState) {
      throw new Error('Consent state not initialized')
    }
    return { ...this.currentConsentState }
  }

  /**
   * Whether the configured CMP has answered at least once.
   *
   * Before the first answer `getCurrentConsentState()` returns the *default*,
   * which for a returning visitor who already consented is wrong — the CMP has
   * simply not restored its stored choice yet. Anything that must not act on a
   * provisional `denied` waits for this. With no CMP configured nothing will
   * ever answer, so it reports settled immediately.
   */
  public hasReceivedConsentSignal(): boolean {
    return this.signalReceived || !this.cmp
  }

  private updateConsentState(newState: ConsentState): void {
    // Set before the dedupe check: a CMP answering exactly the default is still
    // an answer, and the wait has to end.
    this.signalReceived = true

    if (JSON.stringify(this.currentConsentState) === JSON.stringify(newState)) {
      return
    }
    this.currentConsentState = newState

    if (this.config.isDebugMode) {
      console.log('Consent state updated:', newState, '')
    }

    this.notifyConsentUpdate(newState)
  }

  public onConsentUpdate = (callback: (state: ConsentState) => void): void => {
    this.consentUpdateCallbacks.push(callback)
  }

  private notifyConsentUpdate(state: ConsentState): void {
    this.consentUpdateCallbacks.forEach((callback) => callback(state))
  }

  public removeConsentUpdateCallback = (callback: (state: ConsentState) => void): void => {
    this.consentUpdateCallbacks = this.consentUpdateCallbacks.filter((cb) => cb !== callback)
  }

  public destroy(): void {
    this.consentUpdateCallbacks = []

    if (this.cmp) {
      this.cmp.destroy()
      this.cmp = null
    }

    window.removeEventListener('consentUpdate', this.handleCustomConsentUpdate)

    this.initialized = false
    this.signalReceived = false
    this.currentConsentState = null

    if (this.config.isDebugMode) {
      console.log('Destroyed ConsentManager')
    }
  }

  public loadCookieDeclaration(targetElementId: string): void {
    if (this.cmp instanceof CookieBotCMP) {
      this.cmp.loadCookieDeclaration(targetElementId)
    } else if (this.config.isDebugMode) {
      console.warn('Cookie Declaration is only available for CookieBot CMP')
    }
  }
}
